import { useMutation } from "@tanstack/react-query";
import { type ReactNode, type SubmitEvent, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@lawline/ui/components/dialog";
import { Textarea } from "@lawline/ui/components/textarea";

import { sendCommunicationMutation } from "./communication.mutations";
import type { CommunicationType } from "./communication.types";

// Generic "write a message to staff" dialog. `type` tags the message. `ns` is the
// i18n namespace for the copy. `trigger` is the caller's own button, such as a
// sidebar item or a billing CTA.
export function CommunicationDialog({
	type,
	ns,
	trigger,
}: {
	type: CommunicationType;
	ns: "feedback" | "enterpriseRequest";
	trigger: ReactNode;
}) {
	const t = useTranslations(ns);
	const [open, setOpen] = useState(false);
	const [body, setBody] = useState("");
	const { mutateAsync, isPending, isSuccess, isError, reset } =
		useMutation(sendCommunicationMutation);

	function onOpenChange(next: boolean) {
		setOpen(next);
		if (!next) {
			setBody("");
			reset();
		}
	}

	async function submit(e: SubmitEvent) {
		e.preventDefault();
		const trimmed = body.trim();
		if (!trimmed) return;
		try {
			await mutateAsync({ type, body: trimmed });
		} catch {
			// surfaced below via isError
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>{trigger}</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{t("description")}</DialogDescription>
				</DialogHeader>

				{isSuccess ? (
					<div className="flex flex-col gap-4">
						<p className="text-center font-bold">{t("thanks")}</p>
						<DialogFooter>
							<Button onClick={() => onOpenChange(false)}>{t("done")}</Button>
						</DialogFooter>
					</div>
				) : (
					<form onSubmit={submit} className="flex flex-col gap-4">
						<Textarea
							value={body}
							onChange={(e) => setBody(e.target.value)}
							placeholder={t("placeholder")}
							rows={5}
							required
						/>
						{isError && <p className="text-destructive text-sm">{t("error")}</p>}
						<DialogFooter>
							<Button type="submit" disabled={isPending || !body.trim()}>
								{isPending ? t("sending") : t("submit")}
							</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
